import { Avatar } from '@base-ui/react/avatar';
import styled, { css } from 'styled-components';
import { pxToRem } from 'modules/spacing';
import { theme } from 'modules/tetrisTheme';
const containerSize = {
    '2xl': 56,
    xl: 48,
    lg: 40,
    md: 32,
    sm: 24,
    xs: 20,
    xxs: 16,
};
const letterFontSize = {
    '2xl': 18,
    xl: 18,
    lg: 16,
    md: 14,
    sm: 12,
    xs: 10,
    xxs: 8,
};
const iconSize = {
    '2xl': 24,
    xl: 20,
    lg: 16,
    md: 16,
    sm: 12,
    xs: 12,
    xxs: 8,
};
const backgroundColor = {
    primary: theme.swatches.blue[50],
    secondary: theme.swatches.warm[50],
    success: theme.swatches.green[50],
    danger: theme.swatches.red[50],
    warning: theme.swatches.yellow[50],
    info: theme.swatches.blue[50],
    magenta: theme.swatches.magenta[50],
    cyan: theme.swatches.cyan[50],
    orange: theme.swatches.orange[50],
    brown: theme.swatches.brown[50],
    indigo: theme.swatches.indigo[50],
    light: theme.swatches.warm[0],
    dark: theme.swatches.gray[50],
    ghost: `color-mix(in srgb, ${theme.swatches.warm[0]}, transparent 68%)`,
};
const backgroundColorSolid = {
    primary: theme.swatches.blue[500],
    secondary: theme.swatches.warm[500],
    success: theme.swatches.green[500],
    danger: theme.swatches.red[500],
    warning: theme.swatches.yellow[500],
    info: theme.swatches.blue[500],
    magenta: theme.swatches.magenta[500],
    cyan: theme.swatches.cyan[500],
    brown: theme.swatches.brown[500],
    indigo: theme.swatches.indigo[500],
    orange: theme.swatches.orange[500],
    light: theme.swatches.warm[0],
    dark: theme.swatches.gray[1000],
    ghost: `color-mix(in srgb, ${theme.swatches.warm[0]}, transparent 68%)`,
};
const iconColor = {
    primary: theme.swatches.blue[500],
    secondary: theme.swatches.warm[600],
    success: theme.swatches.green[700],
    danger: theme.swatches.red[500],
    warning: theme.swatches.yellow[900],
    info: theme.swatches.blue[600],
    magenta: theme.swatches.magenta[500],
    cyan: theme.swatches.cyan[500],
    brown: theme.swatches.brown[500],
    indigo: theme.swatches.indigo[500],
    orange: theme.swatches.orange[500],
    light: theme.semantic.text[200],
    dark: theme.swatches.gray[0],
    ghost: theme.semantic.text.light,
};
const borderColor = {
    primary: theme.swatches.blue[100],
    secondary: theme.swatches.warm[100],
    success: theme.swatches.green[100],
    danger: theme.swatches.red[100],
    warning: theme.swatches.yellow[100],
    info: theme.swatches.blue[100],
    magenta: theme.swatches.magenta[100],
    cyan: theme.swatches.cyan[100],
    brown: theme.swatches.brown[100],
    indigo: theme.swatches.indigo[100],
    orange: theme.swatches.orange[100],
    light: theme.semantic.border.default,
    dark: theme.semantic.border.default,
    ghost: theme.semantic.text.light,
};
const borderRadius = {
    circle: theme.borderRadius.circle,
    square: theme.borderRadius.md,
};
const solidBorderStyles = css `
  outline: ${({ $appearance }) => {
    const shadowColor = $appearance === 'light' ? borderColor.light : 'transparent';
    return `${pxToRem(1)} solid ${shadowColor}`;
}};
  outline-offset: -${pxToRem(1)};
`;
const borderStyles = css `
  border: ${(props) => `${pxToRem(1)} solid ${borderColor[props.$appearance]}`};
`;
const basicColorStyles = css `
  color: ${(props) => {
    if (props.$appearance === 'light' && props.$solid)
        return theme.semantic.foreground.secondary;
    if (props.$appearance === 'dark' && !props.$solid)
        return theme.semantic.foreground.primary;
    return props.$solid ? theme.semantic.foreground.inverse : iconColor[props.$appearance];
}};
`;
const getBorderStyles = (props) => (props.$solid ? solidBorderStyles : borderStyles);
export const Fallback = styled(Avatar.Fallback) `
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.fontWeight.medium};
  line-height: 1;
`;
export const Root = styled(Avatar.Root) `
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  overflow: hidden;
  user-select: none;
  width: ${(props) => pxToRem(containerSize[props.$size])};
  height: ${(props) => pxToRem(containerSize[props.$size])};
  background-color: ${(props) => props.$solid ? backgroundColorSolid[props.$appearance] : backgroundColor[props.$appearance]};
  ${getBorderStyles};
  border-radius: ${(props) => borderRadius[props.$shape]};

  [data-icon='falcon'] {
    ${basicColorStyles};
    width: ${(props) => pxToRem(iconSize[props.$size])};
    height: ${(props) => pxToRem(iconSize[props.$size])};
  }

  ${Fallback} {
    font-size: ${(props) => pxToRem(letterFontSize[props.$size])};
    text-transform: uppercase;
    ${basicColorStyles}
  }
`;
